/**
 * One answer, opened in a drawer over whatever door page you are on
 * (proposal 1, kanban 059dd612). The owner's complaint: the old panel had no
 * visible top, no close and no actions, and leaving it marked every answer
 * seen. Here the header names who answered, how and when, the question and
 * where it belongs; the reply reads first with the quoted email folded; the
 * footer holds Mark seen / Reply / Add to case findings / Ask again.
 *
 * Opening the drawer NEVER marks the answer seen: only Mark seen does.
 * Escape and × close it; Tab stays inside it; focus returns to where it was.
 */
import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { isActiveCase } from '../portal/investigationsView';
import {
  attachDataRequest,
  markDataRequestSeen,
  messageDataRequest,
  nudgeDataRequest,
  type DoorDataRequestRead,
} from '../../services/qcpDataRequests';
import { appendCaseFindings, type DoorInvestigationRead } from '../../services/qcpInvestigations';
import { trackEvent } from '../../services/portalTracking';
import {
  answeredAtLong,
  askedLine,
  channelPhrase,
  latestResponse,
  splitQuoted,
  type AnswerRow,
} from '../../pages/quality-triage/answersView';
import { AskModal, type AskScope } from './AskModal';
import { AnswerPlace } from './AnswerPlace';
import { DOOR_ITEM_SELECTOR, focusFirst } from '../../pages/quality-triage/doorFocus';
import styles from './AnswerDrawer.module.css';
import { useQualityReadOnly } from '../../context/QualityReadOnlyContext';

export interface AnswerDrawerProps {
  row: AnswerRow;
  investigations: DoorInvestigationRead[];
  userName: string | null;
  onClose: () => void;
  /** Fold a changed ask (seen, replied, attached, nudged) into the list. */
  onUpdated: (request: DoorDataRequestRead) => void;
  /** Fold a changed case (findings added) into the list. */
  onCaseUpdated?: (investigation: DoorInvestigationRead) => void;
}

const FOCUSABLE = 'a[href], button:not([disabled]), textarea, select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

const firstName = (name: string) => name.split(' ')[0] || name;

function errorText(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' ? detail : 'That did not go through. Try again in a moment.';
}

/** Where "Ask again" can send a fresh ask from: the case, else the item. */
function askScope(row: AnswerRow): AskScope | null {
  if (row.investigation) {
    return { kind: 'case', id: row.investigation.id, no: row.investigation.investigation_no, task: null };
  }
  if (row.thread) return { kind: 'thread', id: row.thread.id, no: row.thread.thread_no };
  return null;
}

export function AnswerDrawer({ row, investigations, userName, onClose, onUpdated, onCaseUpdated }: AnswerDrawerProps) {
  // A read-only viewer reads the answer; the desk acts on it (kanban 808dabf6).
  const readOnly = useQualityReadOnly();
  const { request } = row;
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [replyOpen, setReplyOpen] = useState(false);
  const [reply, setReply] = useState('');
  const [askOpen, setAskOpen] = useState(false);

  const ids = {
    data_request_id: request.id,
    investigation_no: row.investigation?.investigation_no ?? null,
    thread_no: row.thread?.thread_no ?? null,
  };

  // Focus the close control on open; give focus back to the opener on close.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => {
      if (opener?.isConnected && opener !== document.body) opener.focus?.();
      else focusFirst([DOOR_ITEM_SELECTOR]);
    };
  }, []);

  const close = (how: 'button' | 'escape' | 'backdrop') => {
    trackEvent('action', 'answer_drawer_close', { ...ids, how, result: 'panel_closed' });
    onClose();
  };

  // Escape closes and Tab stays inside, wherever focus is on the page (a
  // click on the answer text drops focus to the body). The Ask modal, when
  // open on top, owns the keyboard.
  const closeRefFn = useRef(close);
  closeRefFn.current = close;
  useEffect(() => {
    if (askOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeRefFn.current('escape');
        return;
      }
      const panel = panelRef.current;
      if (e.key !== 'Tab' || !panel) return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!panel.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [askOpen]);

  const run = async (label: string, work: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      await work();
      setDone(label);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const last = latestResponse(request);
  // The daily sweep found this answer elsewhere (kanban 3eed0be5): the person
  // asked never replied, so there is nobody on the other end of a reply.
  const foundByMerlin = last?.channel === 'found';
  const { body, quoted } = splitQuoted(last?.body_text ?? '');
  const caseOpen = row.investigation ? isActiveCase(row.investigation) : false;
  const canAttach = investigations.some(isActiveCase);
  const findingsBlocker = !row.investigation
    ? canAttach
      ? 'Attach this answer to a case first: use Attach to a case above.'
      : 'Attach this answer to a case first; no case is open to attach it to.'
    : !caseOpen
      ? `${row.investigation.investigation_no} is ${row.investigation.status}; its findings are closed.`
      : null;
  const scope = askScope(row);
  const isOpenAsk = request.status === 'open';
  const askAgainBlocker = isOpenAsk || scope ? null : 'Attach this answer to a case first, then ask from there.';

  const markSeen = () =>
    run('Marked seen.', async () => {
      trackEvent('action', 'answers_mark_seen', { ...ids, surface: 'drawer', result: 'request_sent' });
      onUpdated(await markDataRequestSeen(request.id));
      // The button turns into a disabled "Seen": focus moves to the close
      // control instead of dropping to the page (proposal 9).
      closeRef.current?.focus();
    });

  const sendReply = () =>
    run(`Sent to ${firstName(row.who)}.`, async () => {
      const text = reply.trim();
      trackEvent('action', 'answer_reply', { ...ids, result: 'request_sent' });
      onUpdated(await messageDataRequest(request.id, text));
      setReply('');
      setReplyOpen(false);
    });

  const addFindings = () =>
    run(`Added to ${row.investigation?.investigation_no ?? 'the case'} findings.`, async () => {
      if (!row.investigation) return;
      trackEvent('action', 'answer_add_findings', { ...ids, result: 'request_sent' });
      const updated = await appendCaseFindings(row.investigation.id, body || (last?.body_text ?? ''), request.id);
      onCaseUpdated?.(updated);
    });

  const askAgain = () => {
    if (isOpenAsk) {
      void run(`Reminder sent to ${firstName(row.who)}.`, async () => {
        trackEvent('action', 'answer_ask_again', { ...ids, how: 'nudge', result: 'request_sent' });
        onUpdated(await nudgeDataRequest(request.id));
      });
      return;
    }
    trackEvent('action', 'answer_ask_again', { ...ids, how: 'new_ask', result: 'modal_opened' });
    setAskOpen(true);
  };

  const attach = (requestId: string, investigationId: string) =>
    run('Attached.', async () => {
      onUpdated(await attachDataRequest(requestId, investigationId));
    });

  return (
    <div className={`${styles.back} ${styles.belowNav}`} onClick={() => close('backdrop')} data-testid="answer-drawer">
      <div
        ref={panelRef}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="answer-drawer-who"
        onClick={(e) => e.stopPropagation()}
      >
        <header className={styles.head}>
          <div className={styles.headText}>
            <div className={styles.eyebrow}>Answer from</div>
            <div>
              <span id="answer-drawer-who" className={styles.who}>
                {row.who}
              </span>
              <span className={styles.how}>
                {channelPhrase(row.channel) && ` · ${channelPhrase(row.channel)}`} · {answeredAtLong(row.at)}
              </span>
            </div>
            <p className={styles.question}>
              to the question <strong>{row.question}</strong>
            </p>
            <div className={styles.meta}>
              {askedLine(row, userName)} ·{' '}
              {!readOnly && <AnswerPlace row={row} investigations={investigations} busy={busy} onAttach={attach} attachOnItem />}
            </div>
          </div>
          <button
            ref={closeRef}
            type="button"
            className={styles.x}
            aria-label="Close answer"
            onClick={() => close('button')}
          >
            ×
          </button>
        </header>

        <div className={styles.body}>
          {body ? (
            <blockquote className={styles.answer} data-testid="answer-body">
              {body}
            </blockquote>
          ) : (
            <p className={styles.empty}>No text came with this answer.</p>
          )}
          {last && last.attachments.length > 0 && (
            <p className={styles.files}>
              {last.attachments.length} file{last.attachments.length > 1 ? 's' : ''} attached:{' '}
              {last.attachments.map((a) => a.filename).join(', ')}
            </p>
          )}
          {quoted.length > 0 && (
            <details className={styles.quoted}>
              <summary>
                Quoted email headers folded · <span className={styles.show}>show</span>
              </summary>
              {quoted.map((q, i) => (
                <pre key={i} className={styles.quotedText}>
                  {q}
                </pre>
              ))}
            </details>
          )}
          <div className={styles.asked}>
            <div className={styles.label}>What was asked</div>
            {/* Asks are written in markdown (the Ask modal and DraftAskCard
                render it the same way); never show raw **emphasis**. */}
            <div className={styles.askedText} data-testid="answer-asked">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{request.question_body}</ReactMarkdown>
            </div>
          </div>
          {replyOpen && !foundByMerlin && (
            <div className={styles.replyBox}>
              <label className={styles.label} htmlFor="answer-drawer-reply">
                Reply to {firstName(row.who)}
              </label>
              <textarea
                id="answer-drawer-reply"
                className={styles.textarea}
                rows={4}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
              />
              <div className={styles.replyActions}>
                <button type="button" className={styles.primary} disabled={busy || !reply.trim()} onClick={() => void sendReply()}>
                  Send
                </button>
                <button type="button" className={styles.secondary} onClick={() => setReplyOpen(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}
          {done && (
            <p className={styles.done} role="status">
              {done}
            </p>
          )}
        </div>

        {!readOnly && (<footer className={styles.foot}>
          <button
            type="button"
            className={styles.primary}
            disabled={busy || !request.answer_unseen}
            title={request.answer_unseen ? undefined : 'Already marked seen'}
            onClick={() => void markSeen()}
          >
            {request.answer_unseen ? 'Mark seen' : 'Seen'}
          </button>
          {!foundByMerlin && (
            <button
              type="button"
              className={styles.secondary}
              disabled={busy}
              aria-expanded={replyOpen}
              onClick={() => setReplyOpen((v) => !v)}
            >
              Reply to {firstName(row.who)}
            </button>
          )}
          <span className={styles.withReason}>
            <button
              type="button"
              className={styles.secondary}
              disabled={busy || findingsBlocker !== null}
              title={findingsBlocker ?? undefined}
              aria-describedby={findingsBlocker ? 'answer-findings-why' : undefined}
              onClick={() => void addFindings()}
            >
              Add to case findings
            </button>
          </span>
          <button
            type="button"
            className={styles.secondary}
            disabled={busy || askAgainBlocker !== null}
            title={askAgainBlocker ?? (isOpenAsk ? 'Send a reminder now' : 'Send a fresh ask with this question')}
            onClick={askAgain}
          >
            Ask again
          </button>
          {(findingsBlocker || askAgainBlocker) && (
            <p id="answer-findings-why" className={styles.why}>
              {findingsBlocker ?? askAgainBlocker}
            </p>
          )}
        </footer>)}
      </div>
      {askOpen && scope && (
        <div onClick={(e) => e.stopPropagation()}>
          <AskModal
            scope={scope}
            desk={request.target_name}
            stepNo={null}
            steps={[]}
            drafts={[]}
            viewerName={userName}
            prefill={{ title: request.question_title, body: request.question_body }}
            onClose={() => setAskOpen(false)}
            onSent={() => {
              setAskOpen(false);
              setDone(`Asked ${firstName(row.who)} again.`);
            }}
            onDiscarded={() => setAskOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

export default AnswerDrawer;
